import { createContext } from 'react';
import { fetcher, Methods } from '../../api';
import { UrlInterface } from '../../types';
import { DetailsContextInterface, State, DetailsInterface } from './types';
import {
  ActionsUrls,
  AuthorType,
  BusinessInfoType,
  ClinicInfoType,
  HospitalInfoType,
  OhcpInfoType,
  PhysicianInfoType,
  ReplyType,
  ReviewsApiResponseType,
  StarsType,
  UserType,
} from '../../types/reviews';
import { createFakeSrcSetObject, dateFormat } from '../../utils';

export const DetailsStateContext = createContext<DetailsContextInterface | undefined>(undefined);

export const initialValues = {
  data: null,
  isDataLoading: true,
  activeTab: {
    id: 0,
    name: null
  },
  participationsDetails: null
} as State;

export const getDetailsData = async (id: string | undefined) => {
  if (id) {
    return fetcher({
      method: Methods.get,
      url: `/dovemap/api/search/documents/${id}/`,
      authTokenRequired: false
    }).then((res) => {
      return res as DetailsInterface;
    }).catch(() => {
      return null;
    })
  }
  return Promise.reject(() => {
    return {
      data: null
    }
  });
}

export const getReviewsData = (reviewsUrl: UrlInterface | null, page: number, userType: any) => {
  if (reviewsUrl) {
    const { method, url } = reviewsUrl;
    return fetcher({
      method,
      url: url + `?page=${page}`
    }).then((res) => {
      return dectectAndFormatReviews(res.results, userType);
    })
  }
  return Promise.reject(() => {
    return []
  })
}

const formatReview = (
  data: ActionsUrls &
    StarsType &
    AuthorType & {
      published: string;
      comment: string;
      overall: number | string;
      id: number;
      reply: ReplyType | null;
    }
) => {
  return {
    details: {
      id: data.id,
      author: {
        username: 'Verified User',
        picture: null,
      },
      comment: data.comment,
      published: dateFormat(data.published),
      overall: Number(Number(data.overall).toFixed(1)) || 0,
    },
    stars: {
      waitTime: data.waitTime,
      friendliness: data.friendliness,
      attentiveness: data.attentiveness,
      qualityTime: data.qualityTime,
      politeness: data.politeness,
      knowledge: data.knowledge,
      appointment: data.appointment,
      lengthOfVisit: data.lengthOfVisit,
      courtesyOfStuff: data.courtesyOfStuff,
      clarity: data.clarity,
      cleanliness: data.cleanliness,
      nursingCare: data.nursingCare,
      friendlinessOfStaff: data.friendlinessOfStaff,
      communityService: data.communityService,
      organizationExpectations: data.organizationExpectations,
      service: data.service,
      valueForMoney: data.valueForMoney,
    },
    actions: {
      reviewCreateFlagUrl: data.reviewCreateFlagUrl,
      reviewDeleteFlagUrl: data.reviewDeleteFlagUrl,
      reviewCreateHelpfulUrl: data.reviewCreateHelpfulUrl,
      reviewDeleteHelpfulUrl: data.reviewDeleteHelpfulUrl,
      reviewCreateUnhelpfulUrl: data.reviewCreateUnhelpfulUrl,
      reviewDeleteUnhelpfulUrl: data.reviewDeleteUnhelpfulUrl,
      reviewReplyCreateUrl: data.reviewReplyCreateUrl,
      reviewReplyDeleteUrl: data.reviewReplyDeleteUrl,
      reviewDeleteUrl: data.reviewDeleteUrl,
    },
    reply: data.reply
      ? {
          ...data.reply,
          published: dateFormat(data.reply.published),
        }
      : null,
  };
};

// map physician API response
export const formatPhysiciansReviews = (data: ReviewsApiResponseType<{ physicianInfo: PhysicianInfoType }>[]) => {
  return data.map((each) => {
    return {
      ...formatReview(each),
      reviewedUser: {
        displayName: each.physicianInfo.fullName,
        picture: createFakeSrcSetObject(each.physicianInfo.picture),
        redirectLink: each.physicianInfo.profileLink,
      },
    };
  });
};

// map hospital API response
export const formatHospitalsReviews = (data: ReviewsApiResponseType<{ hospitalInfo: HospitalInfoType }>[]) => {
  return data.map((each) => {
    return {
      ...formatReview(each),
      reviewedUser: {
        displayName: each.hospitalInfo.hospitalName,
        picture: createFakeSrcSetObject(each.hospitalInfo.picture),
        redirectLink: each.hospitalInfo.profileLink,
      },
    };
  });
};

// map clinic API response
export const formatClinicsReviews = (data: ReviewsApiResponseType<{ clinicInfo: ClinicInfoType }>[]) => {
  return data.map((each) => {
    return {
      ...formatReview(each),
      reviewedUser: {
        displayName: each.clinicInfo.clinicName,
        picture: createFakeSrcSetObject(each.clinicInfo.picture),
        redirectLink: each.clinicInfo.profileLink,
      },
    };
  });
};

// map clinic API response
export const formatOhcpReviews = (data: ReviewsApiResponseType<{ appInfo: OhcpInfoType }>[]) => {
  return data.map((each) => {
    return {
      ...formatReview(each),
      reviewedUser: {
        displayName: each.appInfo.fullName,
        picture: createFakeSrcSetObject(each.appInfo.picture),
        redirectLink: each.appInfo.profileLink,
      },
    };
  });
};

// map business API response
export const formatBusinessReviews = (data: ReviewsApiResponseType<{ businessInfo: BusinessInfoType }>[]) => {
  return data.map((each) => {
    const formattedData = formatReview(each);
    return {
      ...formattedData,
      details: {
        ...formattedData.details,
        published: each.published,
      },
      reviewedUser: {
        displayName: each.businessInfo.name,
        picture: createFakeSrcSetObject(each.businessInfo.picture),
        redirectLink: '/dovebiz' + each.businessInfo.url,
      },
    };
  });
};

// TODO: refactor this

export const dectectAndFormatReviews = (data: ReviewsApiResponseType<any>[], userType: UserType) => {
  switch (userType) {
    case 'physician':
      return formatPhysiciansReviews(data as ReviewsApiResponseType<{ physicianInfo: PhysicianInfoType }>[]);
    case 'hospital':
      return formatHospitalsReviews(data as ReviewsApiResponseType<{ hospitalInfo: HospitalInfoType }>[]);
    case 'clinic':
      return formatClinicsReviews(data as ReviewsApiResponseType<{ clinicInfo: ClinicInfoType }>[]);
    case 'other healthcare':
      return formatOhcpReviews(data as ReviewsApiResponseType<{ appInfo: OhcpInfoType }>[]);
    case 'business':
      return formatBusinessReviews(data as ReviewsApiResponseType<{ businessInfo: BusinessInfoType }>[]);
    default:
      return [];
  }
};
